"use client";
import { CommonCMS, ShoppingBag } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { H } from "@ecco/ui/a11y-heading";
import { Text } from "@ecco/ui/text";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { getPrivacyAttributes } from "@lib/monitoring/privacyAttributes";
import {
    PROFILE_ADD_NEW_ADDRESS_BUTTON_TEST_ID,
    PROFILE_ADDRESS_EDIT_BUTTON_TEST_ID,
    PROFILE_ADDRESS_REMOVE_BUTTON_TEST_ID,
} from "@e2e/helpers/user/constants";

// A line can be a plain string (legacy callers) or a value paired with its
// own data-testid, so the testid travels with the value instead of relying
// on array position.
export type SettingsHeaderLine = string | { value: string; testId?: string };

export interface SettingsHeaderProps {
    data: SettingsHeaderLine[];
    isExpanded: boolean;
    remove?: (id: string) => void;
    title?: string;
    headers: {
        newTitle?: string;
        addAddress?: string;
        subHeading?: string;
        newButton?: string;
        editButton?: string;
        hideEditing?: boolean;
    };
    open?: () => void;
    onClose?: () => void;
    id?: string;
}

export const SettingsHeader = ({
    data,
    isExpanded,
    remove,
    title,
    headers,
    open,
    onClose = () => {},
    id,
}: SettingsHeaderProps) => {
    const { get: getMicrocopy } = useMicrocopy();
    const showEdit = headers.hideEditing ? false : true;

    if (isExpanded) {
        return (
            <Flex
                justifyContent={"space-between"}
                mb={!data && 6}
                alignItems="center"
                mt={!data && 3}
            >
                <H
                    size="h2"
                    fontSize={["mobileBodyB2", "desktopBodyB2"]}
                    textTransform="uppercase"
                    fontWeight="bold"
                >
                    {!data && headers.newTitle}
                </H>
                {/* TODO Remove this button and remove component accordingly to avoid onClose lintern fail */}
                <Button display="none" onClick={onClose}></Button>
            </Flex>
        );
    }

    if (!data && !isExpanded)
        return (
            <Flex data-testid="settingsheader-nodata" flexDirection={"column"}>
                <Box maxW={["100%", "50%"]}>
                    <H size="h2" fontSize={["mobileBodyB2", "desktopBodyB2"]} fontWeight="bold">
                        {headers.addAddress}
                    </H>
                    <Text mt={3}>{headers.subHeading}</Text>
                </Box>
                <Box mt={4} _hover={{ bg: "transparent" }}>
                    <Button
                        rightIcon={<ECCOIcon name="plus" aria-label="plus" size="2xs" />}
                        m={"auto"}
                        p={4}
                        bg={"black"}
                        color={"white"}
                        minW={["100%", "fit-content"]}
                        onClick={open}
                        justifyContent={"center"}
                        data-testid={PROFILE_ADD_NEW_ADDRESS_BUTTON_TEST_ID}
                    >
                        <span>{headers.newButton}</span>
                    </Button>
                </Box>
            </Flex>
        );

    return (
        <Flex
            flexDirection={"column"}
            justifyContent={"space-between"}
            gap={0.5}
            data-testid="settingsheader"
        >
            <Flex justifyContent={"column"} w={"100%"}>
                <Box>
                    {title && (
                        <H
                            size="h2"
                            color="gray.500"
                            fontSize={["mobileBodyB2", "desktopBodyB2"]}
                            fontWeight="bold"
                            textTransform={"uppercase"}
                        >
                            {title}
                        </H>
                    )}
                    {data && (
                        <Box color={"gray.200"} fontWeight={"bold"} pt={3}>
                            {data.map((item, index) => {
                                const value = typeof item === "string" ? item : item.value;
                                const testId = typeof item === "string" ? undefined : item.testId;
                                if (!value) {
                                    return null;
                                }
                                return (
                                    <Text
                                        {...getPrivacyAttributes()}
                                        key={index}
                                        data-testid={testId}
                                        fontSize={["mobileBodyB2", "desktopBodyB2"]}
                                        fontWeight="bold"
                                        color="black"
                                    >
                                        {value}
                                    </Text>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Flex>
            <Flex flexDirection={"column"} mt={[6, 8]} gap={2}>
                {showEdit && (
                    <Button
                        fontWeight={"bold"}
                        color={"gray.500"}
                        onClick={open}
                        variant="link"
                        fontSize={["mobileBodyB2", "desktopBodyB2"]}
                        justifyContent="flex-start"
                        w={"fit-content"}
                        data-testid={PROFILE_ADDRESS_EDIT_BUTTON_TEST_ID}
                        _hover={{
                            textDecoration: "underline",
                            transitionProperty: "common",
                            transitionDuration: "fast",
                            transitionTimingFunction: "ease-out",
                            color: "black",
                            textDecorationColor: "black",
                        }}
                    >
                        <span>{headers.editButton}</span>
                    </Button>
                )}
                {remove && (
                    <Button
                        fontWeight={"bold"}
                        color={"gray.500"}
                        onClick={() => remove(id)}
                        variant="link"
                        fontSize={["mobileBodyB2", "desktopBodyB2"]}
                        justifyContent="flex-start"
                        w={"fit-content"}
                        data-testid={PROFILE_ADDRESS_REMOVE_BUTTON_TEST_ID}
                    >
                        <span>{getMicrocopy(CommonCMS.page, ShoppingBag.removeProduct)}</span>
                    </Button>
                )}
            </Flex>
        </Flex>
    );
};

export default SettingsHeader;
